/**
 * Image viewer with the same comment pins / @mentions / replies / measurements as PDFs
 * (uses pdfAnnotations + pdfMeasurements, pageNumber = 1).
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import SharedWholeProjectNote from '../pm/SharedWholeProjectNote';
import { subscribeToMarkupForDocument, createMarkupStroke, deleteMarkupStroke, updateMarkupStroke } from '../../utils/documentMarkupService';
import { canUseEngineeringMarkup } from '../../utils/userRoles';
import { ENG_STROKE, isEngineeringMarkupTool } from './engineeringMarkup';
import type { DocumentMarkupStroke, DocumentMarkupTool } from '../../types';
import { AnnotationLayer } from './AnnotationLayer';
import { fileCommentNotifiesWithoutMention } from '../../utils/untaggedCommentConfirm';
import { PdfCommentsPopover } from './PdfCommentsPopover';
import { ViewerToolbar, buildDownloadFilename } from './ViewerToolbar';
import { ViewerModeBar, type ViewerInteractionMode } from './ViewerModeBar';
import { useMarkupStrokeWidth } from './markupStrokeWidth';
import { MarkupLayer, type MarkupCommitPayload, type MarkupStrokePatch } from './MarkupLayer';
import { MeasurementLayer, type MeasurementSubMode } from './MeasurementLayer';
import { MeasurementToolbar } from './MeasurementToolbar';
import CalibrationModal, { type CalibrationApplyPayload } from './CalibrationModal';
import { usePdfMeasurements } from '../../hooks/usePdfMeasurements';
import {
  addMeasurement,
  clearPageMeasurements,
  deleteMeasurement,
  deletePageCalibration,
  saveCalibration,
  updateMeasurement,
} from '../../utils/pdfMeasurementService';
import type { DisplayUnit } from '../../utils/measurementMath';
import { getDisplayableImageUrl } from '../../utils/imageDisplayUrl';
import { usePdfAnnotations } from '../../hooks/usePdfAnnotations';
import { captureImagePageContextPreview } from '../../utils/pdfCommentContextCapture';
import { useAuth } from '../../utils/AuthContext';
import { markAnnotationAsRead } from '../../utils/pdfAnnotationService';
import { capPdfRenderScale } from '../../utils/pdfRenderScale';
import { displayTitleForViewer } from '../../utils/projectFileRef';
import { usePreventViewerHistoryBack } from '../../hooks/usePreventViewerHistoryBack';
import { PdfViewerErrorBoundary } from './PdfViewerErrorBoundary';
import {
  VIEWER_FIT_PADDING_PX,
  VIEWER_ZOOM_MAX,
  VIEWER_ZOOM_MIN,
  VIEWER_ZOOM_STEP,
  clampViewerZoom,
  computeViewerFitScale,
} from '../../utils/viewerZoom';
import './PdfViewerModal.css';

const IMAGE_PAGE = 1;

function imageDownloadExt(mime?: string, src?: string): string {
  if (mime?.includes('png')) return '.png';
  if (mime?.includes('jpeg') || mime?.includes('jpg')) return '.jpg';
  if (mime?.includes('webp')) return '.webp';
  if (mime?.includes('gif')) return '.gif';
  if (mime?.includes('svg')) return '.svg';
  const m = src?.match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  if (m) return `.${m[1].toLowerCase()}`;
  return '.png';
}

export interface ImageViewerModalProps {
  url: string;
  title: string;
  documentId: string;
  leadId: string;
  leadName?: string;
  projectId?: string;
  dealId?: string;
  mimeType?: string;
  taskId?: string;
  initialAnnotationId?: string;
  /** This document is the one whole-project file, opened from any structure track. */
  sharedAcrossStructures?: boolean;
  onClose: () => void;
}

function ImageViewerModalInner({
  url,
  title: titleProp,
  documentId,
  leadId,
  projectId,
  taskId,
  mimeType,
  initialAnnotationId,
  sharedAcrossStructures = false,
  onClose,
}: ImageViewerModalProps) {
  const title = displayTitleForViewer(titleProp, url);
  const { user } = useAuth();
  usePreventViewerHistoryBack(true);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const revokeRef = useRef<(() => void) | null>(null);
  const [loadError, setLoadError] = useState<string | null>(() =>
    (url || '').trim() ? null : 'No file URL was provided for this document.',
  );
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [baseScale, setBaseScale] = useState(1);
  const baseScaleRef = useRef(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const initialScaleSetRef = useRef(false);

  const [markupStrokes, setMarkupStrokes] = useState<DocumentMarkupStroke[]>([]);
  const [viewerMode, setViewerMode] = useState<ViewerInteractionMode>('comment');
  const [drawTool, setDrawTool] = useState<DocumentMarkupTool>('pen');
  const [drawColor, setDrawColor] = useState('#3b82f6');
  const { strokeWidth: drawStrokeWidth, setStrokeWidth: setDrawStrokeWidth } = useMarkupStrokeWidth(drawTool);
  const [selectedStampId, setSelectedStampId] = useState('peg');
  const canEngineerMarkup = canUseEngineeringMarkup(user?.role);

  const { calibration, measurements } = usePdfMeasurements(documentId, IMAGE_PAGE);
  const [measureSubMode, setMeasureSubMode] = useState<MeasurementSubMode>('linear');
  const [calibrationModalOpen, setCalibrationModalOpen] = useState(false);
  const [twoPointCapture, setTwoPointCapture] = useState<'inactive' | 'awaitingFirst' | 'awaitingSecond'>('inactive');
  const [twoPointA, setTwoPointA] = useState<{ x: number; y: number } | null>(null);
  const [twoPointB, setTwoPointB] = useState<{ x: number; y: number } | null>(null);
  const [displayUnitOverride, setDisplayUnitOverride] = useState<DisplayUnit | null>(null);
  const [lockedToast, setLockedToast] = useState<string | null>(null);
  const [selectionResetToken, setSelectionResetToken] = useState(0);
  const [pitchMode, setPitchMode] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const captureContextPreview = useCallback(
    async (_pageNumber: number, x: number, y: number) => {
      const marker = user
        ? {
            userName: user.name || user.email || 'Unknown',
            userId: user.id,
            photoURL: user.photoURL,
          }
        : undefined;
      const img = imageRef.current;
      if (!img) return undefined;
      return captureImagePageContextPreview(img, x, y, marker);
    },
    [user],
  );

  const {
    annotations,
    loadingAnnotations,
    focusedAnnotationId,
    setFocusedAnnotationId,
    loadAnnotations,
    handleAddAnnotation,
    handleAddReply,
  } = usePdfAnnotations({
    documentId,
    documentUrl: url,
    leadId,
    projectId,
    taskId,
    user,
    initialAnnotationId,
    annotationPageFilter: IMAGE_PAGE,
    captureContextPreview,
  });

  useEffect(() => {
    const unsub = subscribeToMarkupForDocument(documentId, url, list =>
      setMarkupStrokes(list.filter(s => s.pageNumber === IMAGE_PAGE))
    );
    return () => unsub();
  }, [documentId, url]);

  const onMarkupCommit = useCallback(
    async (payload: MarkupCommitPayload) => {
      if (!user?.id) return;
      try {
        return await createMarkupStroke({
          documentId,
          documentUrl: url,
          leadId,
          ...(projectId ? { projectId } : {}),
          pageNumber: payload.pageNumber,
          tool: payload.tool,
          color: payload.color,
          strokeWidth: payload.strokeWidth,
          points: payload.points,
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
          createdAt: new Date(),
          ...(payload.text != null ? { text: payload.text } : {}),
          ...(payload.fill ? { fill: payload.fill } : {}),
          ...(payload.textColor ? { textColor: payload.textColor } : {}),
          ...(payload.stampId ? { stampId: payload.stampId } : {}),
          ...(typeof payload.stampScale === 'number' ? { stampScale: payload.stampScale } : {}),
          ...(typeof payload.stampRotation === 'number' ? { stampRotation: payload.stampRotation } : {}),
        });
      } catch (err) {
        console.error('Error saving markup:', err);
      }
    },
    [documentId, url, leadId, projectId, user]
  );

  const onMarkupUpdate = useCallback(async (strokeId: string, patch: MarkupStrokePatch) => {
    try {
      await updateMarkupStroke(strokeId, patch);
    } catch (err) {
      console.error('Error updating markup:', err);
    }
  }, []);

  const handleViewerModeChange = useCallback((next: ViewerInteractionMode) => {
    setViewerMode(next);
    if (next === 'engineer') {
      if (!isEngineeringMarkupTool(drawTool) && drawTool !== 'eraser') setDrawTool('callout');
      if (drawColor === '#3b82f6') setDrawColor(ENG_STROKE);
    } else if (next === 'draw') {
      if (isEngineeringMarkupTool(drawTool)) setDrawTool('pen');
      if (drawColor === ENG_STROKE) setDrawColor('#3b82f6');
    }
  }, [drawTool, drawColor]);

  const onMarkupDelete = useCallback(async (strokeId: string) => {
    try {
      await deleteMarkupStroke(strokeId);
    } catch (err) {
      console.error('Error deleting markup:', err);
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (commentsOpen || calibrationModalOpen) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [commentsOpen, calibrationModalOpen, onClose]);

  useEffect(() => {
    if (viewerMode !== 'measure') {
      setPitchMode(false);
      return;
    }
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'p' || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      setPitchMode(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'p') setPitchMode(false);
    };
    const onBlur = () => setPitchMode(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      setPitchMode(false);
    };
  }, [viewerMode]);

  useEffect(() => {
    if (!lockedToast) return;
    const id = setTimeout(() => setLockedToast(null), 3000);
    return () => clearTimeout(id);
  }, [lockedToast]);

  useEffect(() => {
    setDisplayUnitOverride(null);
  }, [calibration?.unitSystem]);

  const onMeasurementCommit = useCallback(
    async (payload: { pageNumber: number; kind: 'linear' | 'polyline' | 'area' | 'angle'; points: { x: number; y: number }[] }) => {
      if (!user?.id) return;
      try {
        await addMeasurement({
          documentId,
          documentUrl: url,
          pageNumber: payload.pageNumber,
          kind: payload.kind,
          points: payload.points,
          ...(leadId ? { leadId } : {}),
          ...(projectId ? { projectId } : {}),
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
        });
      } catch (err) {
        console.error('Error saving measurement:', err);
      }
    },
    [documentId, url, leadId, projectId, user],
  );

  const onMeasurementUpdate = useCallback(async (id: string, points: { x: number; y: number }[]) => {
    try {
      await updateMeasurement(id, { points });
    } catch (err) {
      console.error('Error updating measurement:', err);
    }
  }, []);

  const onMeasurementDelete = useCallback(async (id: string) => {
    try {
      await deleteMeasurement(id);
    } catch (err) {
      console.error('Error deleting measurement:', err);
    }
  }, []);

  const onClearPageMeasurements = useCallback(async () => {
    const ok = window.confirm('Delete every measurement on this page?');
    if (!ok) return;
    try {
      await clearPageMeasurements(documentId, IMAGE_PAGE);
      setSelectionResetToken(t => t + 1);
    } catch (err) {
      console.error('Error clearing measurements:', err);
    }
  }, [documentId]);

  const onClearCalibration = useCallback(async () => {
    if (!calibration?.id) return;
    const ok = window.confirm(
      'Remove the scale on this page? Existing measurement labels will read "(no scale)" until a new scale is set.',
    );
    if (!ok) return;
    try {
      await deletePageCalibration(calibration.id);
    } catch (err) {
      console.error('Error clearing calibration:', err);
    }
  }, [calibration?.id]);

  const onApplyCalibration = useCallback(
    async (payload: CalibrationApplyPayload) => {
      const nw = Math.max(1, imgNatural.w);
      const nh = Math.max(1, imgNatural.h);
      const base = {
        documentId,
        documentUrl: url,
        mode: payload.mode,
        standardScaleId: payload.standardScaleId,
        pointA: payload.pointA,
        pointB: payload.pointB,
        realDistance: payload.realDistance,
        realDistanceUnit: payload.realDistanceUnit,
        realPerPdfPoint: payload.realPerPdfPoint,
        unitSystem: payload.unitSystem,
        baseUnit: payload.baseUnit,
        displayUnit: payload.displayUnit,
        pageNaturalWidth: nw,
        pageNaturalHeight: nh,
        ...(leadId ? { leadId } : {}),
        ...(projectId ? { projectId } : {}),
        ...(user?.id ? { userId: user.id } : {}),
        ...(user ? { userName: user.name || user.email || 'Unknown' } : {}),
      };
      await saveCalibration({ ...base, pageNumber: IMAGE_PAGE });
      setTwoPointA(null);
      setTwoPointB(null);
      setTwoPointCapture('inactive');
    },
    [documentId, url, imgNatural.w, imgNatural.h, leadId, projectId, user],
  );

  const handleRequestTwoPointCapture = useCallback(() => {
    setCalibrationModalOpen(false);
    setTwoPointA(null);
    setTwoPointB(null);
    setTwoPointCapture('awaitingFirst');
  }, []);

  const handleCaptureClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const p = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
      if (twoPointCapture === 'awaitingFirst') {
        setTwoPointA(p);
        setTwoPointCapture('awaitingSecond');
      } else if (twoPointCapture === 'awaitingSecond') {
        setTwoPointB(p);
        setTwoPointCapture('inactive');
        setCalibrationModalOpen(true);
      }
    },
    [twoPointCapture],
  );

  useEffect(() => {
    let cancelled = false;
    setDisplayUrl(null);
    setImgNatural({ w: 0, h: 0 });
    initialScaleSetRef.current = false;
    revokeRef.current?.();
    revokeRef.current = null;

    const trimmed = (url || '').trim();
    if (!trimmed) {
      setLoadError('No file URL was provided for this document.');
      return;
    }
    setLoadError(null);

    (async () => {
      try {
        const { url: resolved, revoke } = await getDisplayableImageUrl(trimmed, mimeType);
        if (cancelled) {
          revoke();
          return;
        }
        revokeRef.current = revoke;
        setDisplayUrl(resolved);
      } catch {
        if (!cancelled) setLoadError('Could not load this image. Try downloading the file.');
      }
    })();

    return () => {
      cancelled = true;
      revokeRef.current?.();
      revokeRef.current = null;
    };
  }, [url, mimeType]);

  useEffect(() => {
    if (!displayUrl) return;
    const img = new Image();
    img.onload = () => {
      setImgNatural({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    };
    img.onerror = () => setLoadError('Could not display this image.');
    img.src = displayUrl;
  }, [displayUrl]);

  const handleSelectComment = useCallback(
    (annotationId: string, page: number) => {
      if (page !== IMAGE_PAGE) return;
      setFocusedAnnotationId(annotationId);
      if (user?.id) {
        void markAnnotationAsRead(annotationId, user.id);
      }
    },
    [setFocusedAnnotationId, user?.id]
  );

  const effectiveScale = (() => {
    if (!fitMode || !canvasWrapRef.current || imgNatural.w <= 0) return scale;
    const wrap = canvasWrapRef.current;
    const w = wrap.clientWidth - VIEWER_FIT_PADDING_PX;
    const h = wrap.clientHeight - VIEWER_FIT_PADDING_PX;
    return computeViewerFitScale(w, h, imgNatural.w, imgNatural.h) ?? scale;
  })();

  const displayScale = fitMode ? effectiveScale : scale;

  const targetRenderScale = useMemo(
    () => capPdfRenderScale(displayScale, imgNatural.w, imgNatural.h),
    [displayScale, imgNatural.w, imgNatural.h]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (Math.abs(targetRenderScale - baseScaleRef.current) < 0.001) return;
    if (!initialScaleSetRef.current) {
      initialScaleSetRef.current = true;
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
      return;
    }
    debounceRef.current = setTimeout(() => {
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [targetRenderScale]);

  const zoomRatio = displayScale / baseScale;
  const visualWidth = imgNatural.w * displayScale;
  const visualHeight = imgNatural.h * displayScale;

  useEffect(() => {
    if (!fitMode || !canvasWrapRef.current || imgNatural.w <= 0) return;
    const wrap = canvasWrapRef.current;
    const ro = new ResizeObserver(() => setScale(s => s));
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fitMode, imgNatural.w, imgNatural.h]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        setFitMode(false);
        const delta = -e.deltaY * 0.002;
        setScale(s => clampViewerZoom(s + delta));
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true });
  }, []);

  const zoomIn = () => {
    setFitMode(false);
    setScale(s => clampViewerZoom(s * VIEWER_ZOOM_STEP));
  };
  const zoomOut = () => {
    setFitMode(false);
    setScale(s => clampViewerZoom(s / VIEWER_ZOOM_STEP));
  };
  const zoomToFit = () => setFitMode(true);

  const markupInteractive = viewerMode === 'draw' || (viewerMode === 'engineer' && canEngineerMarkup);
  const annotationInteractive = viewerMode === 'comment';
  const measureInteractive = viewerMode === 'measure' && twoPointCapture === 'inactive';
  const captureInteractive = twoPointCapture !== 'inactive';
  const pageToolsElevated =
    markupInteractive || measureInteractive || twoPointCapture !== 'inactive';
  const pageTransformZIndex = pageToolsElevated ? 10 : 1;
  const annotationOverlayZIndex = annotationInteractive ? 10 : 2;

  return (
    <div className="pdf-viewer-modal-overlay" onClick={onClose}>
      <div className="pdf-viewer-modal" onClick={e => e.stopPropagation()}>
        <header className="pdf-viewer-header">
          <h2 className="pdf-viewer-title">{title}</h2>
          <div className="pdf-viewer-actions">
            <button type="button" className="pdf-viewer-close-btn" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </header>

        {sharedAcrossStructures ? <SharedWholeProjectNote /> : null}

        <div className="pdf-viewer-body">
          <div className="pdf-viewer-main">
            <ViewerModeBar
              mode={viewerMode}
              onModeChange={handleViewerModeChange}
              drawTool={drawTool}
              onDrawToolChange={setDrawTool}
              drawColor={drawColor}
              onDrawColorChange={setDrawColor}
              drawStrokeWidth={drawStrokeWidth}
              onDrawStrokeWidthChange={setDrawStrokeWidth}
              showEngineering={canEngineerMarkup}
              selectedStampId={selectedStampId}
              onStampChange={setSelectedStampId}
            />
            {viewerMode === 'measure' && (
              <MeasurementToolbar
                subMode={measureSubMode}
                onSubModeChange={setMeasureSubMode}
                calibration={calibration}
                pageNumber={IMAGE_PAGE}
                onOpenCalibration={() => setCalibrationModalOpen(true)}
                onClearCalibration={onClearCalibration}
                onClearMeasurements={onClearPageMeasurements}
                displayUnitOverride={displayUnitOverride}
                onDisplayUnitOverrideChange={setDisplayUnitOverride}
                pitchMode={pitchMode}
              />
            )}
            {captureInteractive && (
              <div className="pdf-capture-banner">
                {twoPointCapture === 'awaitingFirst'
                  ? 'Click the first point of a known dimension on the page.'
                  : 'Now click the second point.'}
                <button
                  type="button"
                  className="pdf-capture-cancel"
                  onClick={() => {
                    setTwoPointCapture('inactive');
                    setTwoPointA(null);
                    setTwoPointB(null);
                    setCalibrationModalOpen(true);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <div className="pdf-viewer-canvas-wrap" ref={canvasWrapRef}>
              {!displayUrl && !loadError && (
                <div className="pdf-viewer-loading">
                  <span>Loading image…</span>
                </div>
              )}
              {loadError && (
                <div className="pdf-viewer-error">
                  <span>{loadError}</span>
                </div>
              )}
              {displayUrl && !loadError && imgNatural.w > 0 && (
                <div className="pdf-page-container" style={{ width: visualWidth, height: visualHeight }}>
                  <div
                    className="pdf-page-transform"
                    style={{
                      ...(zoomRatio !== 1 ? { transform: `scale(${zoomRatio})`, transformOrigin: 'top left' } : {}),
                      zIndex: pageTransformZIndex,
                    }}
                  >
                    <img
                      ref={imageRef}
                      src={displayUrl}
                      alt=""
                      className="image-viewer-img"
                      crossOrigin="anonymous"
                      draggable={false}
                      style={{
                        width: imgNatural.w * baseScale,
                        height: imgNatural.h * baseScale,
                        display: 'block',
                        verticalAlign: 'top',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: markupInteractive ? 10 : 2,
                        pointerEvents: markupInteractive ? 'auto' : 'none',
                      }}
                    >
                      <MarkupLayer
                        pageNumber={IMAGE_PAGE}
                        scale={displayScale}
                        strokes={markupStrokes}
                        activeTool={drawTool}
                        color={drawColor}
                        baseStrokeWidth={drawStrokeWidth}
                        enabled={markupInteractive}
                        currentUserId={user?.id}
                        onCommitStroke={onMarkupCommit}
                        onDeleteStroke={onMarkupDelete}
                        onUpdateStroke={onMarkupUpdate}
                        selectedStampId={selectedStampId}
                        canEraseAnyEngineering={canEngineerMarkup}
                      />
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: measureInteractive ? 10 : 4,
                        pointerEvents: measureInteractive ? 'auto' : 'none',
                      }}
                    >
                      <MeasurementLayer
                        pageNumber={IMAGE_PAGE}
                        pageNaturalWidth={Math.max(1, imgNatural.w)}
                        pageNaturalHeight={Math.max(1, imgNatural.h)}
                        visualWidth={visualWidth}
                        visualHeight={visualHeight}
                        measurements={measurements}
                        calibration={calibration}
                        subMode={measureSubMode}
                        displayUnitOverride={displayUnitOverride || undefined}
                        pitchMode={pitchMode}
                        enabled={measureInteractive}
                        onLockedAttempt={() => setLockedToast('Set a scale before measuring.')}
                        onCommit={onMeasurementCommit}
                        onUpdatePoints={onMeasurementUpdate}
                        onDelete={onMeasurementDelete}
                        selectionResetToken={selectionResetToken}
                      />
                    </div>
                    {captureInteractive && (() => {
                      const safeVw = Math.max(50, visualWidth);
                      const safeVh = Math.max(50, visualHeight);
                      const sx = 100 / safeVw;
                      const sy = 100 / safeVh;
                      return (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 7 }}>
                          <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            style={{ width: '100%', height: '100%', cursor: 'crosshair', touchAction: 'none' }}
                            onClick={handleCaptureClick}
                          >
                            {twoPointA && (
                              <g transform={`translate(${twoPointA.x},${twoPointA.y}) scale(${sx},${sy})`}>
                                <circle r={9} fill="none" stroke="#3b82f6" strokeWidth={1.5} />
                                <circle r={4} fill="#3b82f6" stroke="#fff" strokeWidth={1.2} />
                              </g>
                            )}
                          </svg>
                        </div>
                      );
                    })()}
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: annotationOverlayZIndex,
                      pointerEvents: annotationInteractive ? 'auto' : 'none',
                    }}
                  >
                    <AnnotationLayer
                      pageNumber={IMAGE_PAGE}
                      scale={displayScale}
                      annotations={annotations}
                      loading={loadingAnnotations}
                      interactive={annotationInteractive}
                      onAddAnnotation={handleAddAnnotation}
                      onAddReply={handleAddReply}
                      onDeleteAnnotation={loadAnnotations}
                      onAnnotationMoved={loadAnnotations}
                      currentUserId={user?.id}
                      focusAnnotationId={focusedAnnotationId}
                      onFocusAnnotation={setFocusedAnnotationId}
                      onAnnotationViewed={loadAnnotations}
                      notifiesWithoutMention={fileCommentNotifiesWithoutMention(leadId, documentId)}
                    />
                  </div>
                </div>
              )}
              {lockedToast && (
                <div className="pdf-locked-toast">
                  {lockedToast}
                  <button
                    type="button"
                    className="pdf-locked-toast-action"
                    onClick={() => {
                      setLockedToast(null);
                      setCalibrationModalOpen(true);
                    }}
                  >
                    Set scale
                  </button>
                </div>
              )}
            </div>

            <ViewerToolbar
              displayScale={displayScale}
              fitMode={fitMode}
              commentsOpen={commentsOpen}
              annotationCount={annotations.length}
              onCommentsOpen={() => setCommentsOpen(true)}
              onZoomOut={zoomOut}
              onZoomIn={zoomIn}
              onZoomToFit={zoomToFit}
              minScale={VIEWER_ZOOM_MIN}
              maxScale={VIEWER_ZOOM_MAX}
              downloadUrl={url}
              downloadFilename={buildDownloadFilename(title, imageDownloadExt(mimeType, url))}
            />
          </div>
        </div>
      </div>

      {commentsOpen && (
        <PdfCommentsPopover
          annotations={annotations}
          currentPageNumber={IMAGE_PAGE}
          currentUserId={user?.id}
          onSelectComment={handleSelectComment}
          onClose={() => setCommentsOpen(false)}
        />
      )}

      {calibrationModalOpen &&
        createPortal(
          <CalibrationModal
            pageNumber={IMAGE_PAGE}
            pageNaturalWidth={Math.max(1, imgNatural.w)}
            pageNaturalHeight={Math.max(1, imgNatural.h)}
            numPages={1}
            existing={calibration}
            capturedPoints={twoPointA && twoPointB ? { a: twoPointA, b: twoPointB } : null}
            onRequestTwoPointCapture={handleRequestTwoPointCapture}
            onApply={onApplyCalibration}
            onClose={() => setCalibrationModalOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}

export default function ImageViewerModal(props: ImageViewerModalProps) {
  return (
    <PdfViewerErrorBoundary onClose={props.onClose}>
      <ImageViewerModalInner {...props} />
    </PdfViewerErrorBoundary>
  );
}
